import { useState, useCallback, useEffect, useRef } from 'react';
import { safeJsonParse } from '../lib/utils';

/**
 * A hook that persists state to localStorage
 * 
 * Performance optimization: Uses default value for initial render, then hydrates
 * from localStorage via useEffect to avoid blocking the main thread during
 * initial page load. This improves TBT (Total Blocking Time).
 * 
 * @param key - The localStorage key
 * @param defaultValue - The default value if nothing is stored
 * @returns A tuple of [state, setState] similar to useState
 */
export function usePersistedState<T>(
  key: string,
  defaultValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  // Start with default value to avoid blocking initial render
  const [state, setState] = useState<T>(defaultValue);
  const isInitialized = useRef(false);

  // Hydrate from localStorage after initial render (non-blocking)
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      try {
        const stored = localStorage.getItem(key);
        if (stored !== null) {
          const parsed = safeJsonParse(stored, defaultValue);
          // Only update if different from default to avoid unnecessary re-render
          if (JSON.stringify(parsed) !== JSON.stringify(defaultValue)) {
            setState(parsed);
          }
        }
      } catch {
        // localStorage might not be available (SSR, private browsing, etc.)
        console.debug(`usePersistedState: Could not read from localStorage for key "${key}"`);
      }
    }
  }, [key, defaultValue]);

  const setPersistedState = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState((prev) => {
        const next = typeof value === 'function' ? (value as (prev: T) => T)(prev) : value;
        try {
          localStorage.setItem(key, JSON.stringify(next));
        } catch {
          // localStorage might be full or unavailable
          console.debug(`usePersistedState: Could not write to localStorage for key "${key}"`);
        }
        return next;
      });
    },
    [key]
  );

  return [state, setPersistedState];
}

